import isEmpty from 'lodash/isEmpty';

import { GLOBAL_ASK_AICA_CONSTANTS } from './constants';
import { GlobalAskAicaState, BackgroundThread, GateOutcome, GATE_OUTCOME_TYPE } from './types';
import { DEFAULT_GLOBAL_SOURCES, DRAFT_CONVERSATION_KEY } from './constants';
import { accumulateReasoning } from '../../utils/reasoningUtils';
import { ClarificationCard } from '../dashboard/askAica/clarificationTypes';

const initialState: GlobalAskAicaState = {
    messages: [],
    isLoading: false,
    isLoadingChatHistory: false,
    error: null,
    currentCompany: null,
    companyLess: false,
    askBack: null,
    decline: null,
    conversationId: null,
    conversations: [],
    isLoadingConversations: false,
    selectedSources: DEFAULT_GLOBAL_SOURCES,
    isClearingChat: false,
    hasMoreData: false,
    pendingNewConversation: false,
    background: {},
};

/** The active conversation's background key. A New Chat has no server id until its first
 * chunk lands, so it parks under DRAFT_CONVERSATION_KEY. */
const activeKey = (state: GlobalAskAicaState): string => state.conversationId ?? DRAFT_CONVERSATION_KEY;

/** True when an action names a conversation that is NOT the one on screen — its write
 * belongs in a background buffer, not in the visible thread. */
const targetsBackground = (state: GlobalAskAicaState, conversationKey?: string | null): boolean =>
    !!conversationKey && conversationKey !== activeKey(state);

const EMPTY_THREAD: BackgroundThread = { messages: [], isLoading: false, reasoning: undefined };

/** Applies `mutate` to the named background buffer, leaving the visible thread alone. */
const patchBackground = (
    state: GlobalAskAicaState,
    conversationKey: string,
    mutate: (thread: BackgroundThread) => BackgroundThread,
): GlobalAskAicaState => ({
    ...state,
    background: {
        ...state.background,
        [conversationKey]: mutate(state.background[conversationKey] ?? EMPTY_THREAD),
    },
});

const addMessage = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => {
    if (targetsBackground(state, action.conversationKey)) {
        return patchBackground(state, action.conversationKey, (thread) => ({
            ...thread,
            messages: [...thread.messages, action.payload],
        }));
    }
    return { ...state, messages: [...state.messages, action.payload] };
};

/** Both sources and answer_scope arrive only on the FINAL chunk, so every
 * interim content-only update must leave whatever is already there alone —
 * merge-if-present rather than assign (mirrors leads
 * askAica/reducer.ts::updateMessage); metadata identity stays stable when
 * neither field is in this dispatch. */
const applyMessagePatch = (
    messages: any[],
    id: number | string,
    content: string | undefined,
    sources?: any,
    answerScope?: any,
    patch?: { status?: string; userMessageId?: string; clarification?: ClarificationCard },
) => {
    const metadataPatch = {
        ...(sources ? { sources } : {}),
        ...(answerScope ? { answer_scope: answerScope } : {}),
        ...(patch?.userMessageId ? { user_message_id: patch.userMessageId } : {}),
        ...(patch?.clarification ? { clarification: patch.clarification } : {}),
    };
    const hasMetadataPatch = Object.keys(metadataPatch).length > 0;
    return messages.map((message) =>
        message.id === id
            ? {
                  ...message,
                  content: content ?? message.content,
                  metadata: hasMetadataPatch ? { ...message.metadata, ...metadataPatch } : message.metadata,
                  // Both the re-attach path and the live final chunk send a status
                  // (see askAica/reducer.ts).
                  status: patch?.status ?? message.status,
              }
            : message,
    );
};

const updateMessage = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => {
    const { id, content, sources, answerScope, patch } = action.payload;
    if (targetsBackground(state, action.conversationKey)) {
        return patchBackground(state, action.conversationKey, (thread) => ({
            ...thread,
            messages: applyMessagePatch(thread.messages, id, content, sources, answerScope, patch),
        }));
    }
    return {
        ...state,
        messages: applyMessagePatch(state.messages, id, content, sources, answerScope, patch),
    };
};

/** Applies `transform` to the named conversation's messages — the visible thread's, or its
 * background buffer's when the write targets a conversation that is not on screen. */
const mapThreadMessages = (
    state: GlobalAskAicaState,
    conversationKey: string | null | undefined,
    transform: (messages: any[]) => any[],
): GlobalAskAicaState => {
    if (targetsBackground(state, conversationKey)) {
        return patchBackground(state, conversationKey as string, (thread) => ({ ...thread, messages: transform(thread.messages) }));
    }
    return { ...state, messages: transform(state.messages) };
};

/** Uses the shared accumulateReasoning helper (also used by the leads
 * askAica/reducer.ts::setReasoning) to accumulate the streamed reasoning
 * message onto the in-flight turn's reasoning — either the visible thread's
 * or, if the turn belongs to a background conversation, its buffer's. */
const setReasoning = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => {
    const { reasoning, hasNewReasoning } = action.payload;
    if (isEmpty(reasoning) || !hasNewReasoning) return state;
    if (targetsBackground(state, action.conversationKey)) {
        return patchBackground(state, action.conversationKey, (thread) => ({
            ...thread,
            reasoning: accumulateReasoning(reasoning),
        }));
    }
    return {
        ...state,
        reasoning: accumulateReasoning(reasoning),
    };
};

const initialiseMessageThread = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => ({
    ...state,
    messages: action.payload,
    isLoadingChatHistory: false,
});

/**
 * Maps a raw gate_outcome (as read off the cumulative SSE metadata) onto the
 * individual gate-state fields. Only one of askBack/decline is ever non-null
 * at a time — a new gate outcome always supersedes the previous one.
 *
 * - resolved/continue/portfolio: the company is settled — set the chip, clear any
 *   open picker/notice.
 * - company_less: the chip is keyed off the wire's current_company, NOT the
 *   outcome type — a company_less turn (a general question, or a failed
 *   switch) can still carry a PINNED company on gate_outcome.current_company,
 *   and the chip must stay through it (RECUR-27369 R-2). Only a genuinely
 *   null/absent current_company clears the chip.
 * - ask_back: candidates offered (kind:"pick"). currentCompany/companyLess are
 *   left UNTOUCHED so a prior chip stays visible while the picker is open.
 * - decline: same as company_less — keyed off the wire's current_company, so a
 *   decline while a company is pinned keeps the chip; a decline with no prior
 *   company shows none.
 */
const applyGateOutcome = (state: GlobalAskAicaState, gateOutcome: GateOutcome): GlobalAskAicaState => {
    switch (gateOutcome.type) {
        case GATE_OUTCOME_TYPE.RESOLVED:
        case GATE_OUTCOME_TYPE.CONTINUE:
        case GATE_OUTCOME_TYPE.PORTFOLIO:
            return {
                ...state,
                currentCompany: gateOutcome.current_company,
                companyLess: false,
                askBack: null,
                decline: null,
            };
        case GATE_OUTCOME_TYPE.COMPANY_LESS:
            return {
                ...state,
                currentCompany: gateOutcome.current_company ?? null,
                companyLess: true,
                askBack: null,
                decline: null,
            };
        case GATE_OUTCOME_TYPE.ASK_BACK:
            return {
                ...state,
                askBack: gateOutcome.ask_back ?? null,
                decline: null,
            };
        case GATE_OUTCOME_TYPE.DECLINE:
            return {
                ...state,
                currentCompany: gateOutcome.current_company ?? null,
                decline: gateOutcome.decline ?? null,
                askBack: null,
            };
        default:
            return state;
    }
};

/** Gate-resolution fields reset by both New Chat and loading a different past
 * conversation — neither should carry a stale resolved company into the next turn. */
const clearGateState = (): Pick<GlobalAskAicaState, 'currentCompany' | 'companyLess' | 'askBack' | 'decline'> => ({
    currentCompany: null,
    companyLess: false,
    askBack: null,
    decline: null,
});

// pendingNewConversation is deliberately NOT reset here — newChat() dispatches
// it explicitly right after RESET_GLOBAL_CHAT, and order matters (§2 D2): a
// bare reset (e.g. company-less clearCompany) must not arm New Chat's
// force-create on its own.
const resetGlobalChat = (state: GlobalAskAicaState): GlobalAskAicaState => ({
    ...state,
    ...clearGateState(),
    messages: [],
    isLoading: false,
    isLoadingChatHistory: false,
    error: null,
    conversationId: null,
    reasoning: undefined,
    hasMoreData: false,
});

/**
 * Park + restore in one step. The outgoing conversation is only parked while its turn is
 * still streaming — a finished thread is dropped so buffers can't grow without bound. If
 * the incoming conversation has a parked turn, it becomes the visible thread again and
 * its buffer is drained, so the caller must NOT then fetch history over the top of it.
 */
const switchConversation = (state: GlobalAskAicaState, nextId: string | null): GlobalAskAicaState => {
    const background = { ...state.background };
    const outgoing = activeKey(state);

    // A draft is never parked: it has no sidebar row to return to, and two consecutive
    // New Chats would collide on the same key.
    if (state.isLoading && outgoing !== DRAFT_CONVERSATION_KEY) {
        background[outgoing] = { messages: state.messages, isLoading: true, reasoning: state.reasoning };
    } else {
        delete background[outgoing];
    }

    const incoming = nextId ? background[nextId] : undefined;
    if (nextId && incoming) delete background[nextId];

    return {
        ...state,
        background,
        conversationId: nextId,
        messages: incoming ? incoming.messages : [],
        isLoading: incoming ? incoming.isLoading : false,
        reasoning: incoming ? incoming.reasoning : undefined,
        pendingNewConversation: false,
    };
};

const setConversations = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => ({
    ...state,
    conversations: action.payload,
    isLoadingConversations: false,
});

const globalAskAicaReducer = (state: GlobalAskAicaState = initialState, action: any): GlobalAskAicaState => {
    switch (action.type) {
        case GLOBAL_ASK_AICA_CONSTANTS.ADD_MESSAGE:
            return addMessage(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.UPDATE_MESSAGE:
            return updateMessage(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.UPDATE_MESSAGE_METADATA:
            return mapThreadMessages(state, action.conversationKey, (messages) =>
                messages.map((message) =>
                    message.id === action.payload.id
                        ? { ...message, metadata: { ...message.metadata, ...action.payload.metadataPatch } }
                        : message,
                ),
            );
        case GLOBAL_ASK_AICA_CONSTANTS.REMOVE_MESSAGES:
            return mapThreadMessages(state, action.conversationKey, (messages) =>
                messages.filter((message) => !action.payload.includes(message.id)),
            );
        case GLOBAL_ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD:
            return initialiseMessageThread(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.SET_IS_LOADING:
            return targetsBackground(state, action.conversationKey)
                ? patchBackground(state, action.conversationKey, (thread) => ({ ...thread, isLoading: action.payload }))
                : { ...state, isLoading: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY:
            return { ...state, isLoadingChatHistory: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_ERROR:
            return { ...state, error: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_REASONING:
            return setReasoning(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.RESET_REASONING:
            return { ...state, reasoning: undefined };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_GATE_OUTCOME:
            return applyGateOutcome(state, action.payload);
        case GLOBAL_ASK_AICA_CONSTANTS.SET_CURRENT_COMPANY:
            return { ...state, currentCompany: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_ASK_BACK:
            return { ...state, askBack: action.payload };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_CONVERSATION_ID:
            // A conversation id landing — whether captured off a live turn's
            // first chunk, or loaded explicitly via loadConversation — always
            // supersedes any pending New Chat intent (D2).
            return { ...state, conversationId: action.payload, pendingNewConversation: false };
        case GLOBAL_ASK_AICA_CONSTANTS.SWITCH_CONVERSATION:
            return switchConversation(state, action.payload);
        case GLOBAL_ASK_AICA_CONSTANTS.RESET_GLOBAL_CHAT:
            return resetGlobalChat(state);
        case GLOBAL_ASK_AICA_CONSTANTS.RESET_GATE_STATE:
            return { ...state, ...clearGateState() };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_PENDING_NEW_CONVERSATION:
            return { ...state, pendingNewConversation: action.payload };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_CONVERSATIONS:
            return setConversations(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.SET_LOADING_CONVERSATIONS:
            return { ...state, isLoadingConversations: action.payload };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT:
            return { ...state, isClearingChat: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_HAS_MORE_DATA:
            return { ...state, hasMoreData: action.payload };

        default:
            return state;
    }
};

export default globalAskAicaReducer;
